import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, ImageIcon, RotateCcw, RotateCw, Loader2 } from "lucide-react";
import { normalizeImageFile, rotateImageFile } from "../../lib/imageNormalize";

export default function AiFileDropzone({ onFile, accept = "image/*,.pdf", label }) {
    const inputRef = useRef(null);
    const [drag, setDrag] = useState(false);
    const [preview, setPreview] = useState(null);
    const [name, setName] = useState("");
    const [currentFile, setCurrentFile] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    const applyFile = useCallback(
        (file) => {
            if (!file) return;
            setCurrentFile(file);
            setName(file.name);
            if (file.type.startsWith("image/")) {
                setPreview((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return URL.createObjectURL(file);
                });
            } else {
                setPreview((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return null;
                });
            }
            onFile?.(file);
        },
        [onFile],
    );

    const handleFile = useCallback(
        async (file) => {
            if (!file) return;
            setBusy(true);
            try {
                let next = file;
                if (file.type.startsWith("image/")) {
                    next = await normalizeImageFile(file);
                }
                applyFile(next);
            } finally {
                setBusy(false);
            }
        },
        [applyFile],
    );

    const handleRotate = async (degrees) => {
        if (!currentFile?.type?.startsWith("image/")) return;
        setBusy(true);
        try {
            const rotated = await rotateImageFile(currentFile, degrees);
            applyFile(rotated);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className={`rounded-2xl border-2 border-dashed p-8 text-center transition ${
                drag
                    ? "border-[#D4AF37] bg-[#D4AF37]/5"
                    : "border-white/15 bg-white/[0.02] hover:border-white/25"
            }`}
            onDragOver={(e) => {
                e.preventDefault();
                setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
                e.preventDefault();
                setDrag(false);
                const f = e.dataTransfer.files?.[0];
                handleFile(f);
            }}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {preview ? (
                <img
                    src={preview}
                    alt="Önizleme"
                    className="mx-auto max-h-48 rounded-lg border border-white/10 mb-4 object-contain"
                />
            ) : (
                <div className="mx-auto h-16 w-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                    <ImageIcon className="h-8 w-8 text-slate-500" />
                </div>
            )}
            <p className="text-sm text-slate-300 mb-1">
                {label || "Ruhsat, kimlik, kaza tutanağı veya PDF sürükleyin"}
            </p>
            <p className="text-xs text-slate-500 mb-4">
                {name || "PNG, JPG veya PDF"}
                {busy ? " — hazırlanıyor…" : ""}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => inputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#D4AF37]/15 text-[#F3E5AB] border border-[#D4AF37]/30 text-sm font-semibold hover:bg-[#D4AF37]/25 transition disabled:opacity-50"
                >
                    <Upload className="h-4 w-4" />
                    Dosya Seç
                </button>
                {preview ? (
                    <>
                        <button
                            type="button"
                            disabled={busy}
                            title="Sola döndür"
                            onClick={() => handleRotate(-90)}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-white/15 text-slate-300 text-xs hover:text-white disabled:opacity-50"
                        >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Döndür
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            title="Sağa döndür"
                            onClick={() => handleRotate(90)}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-white/15 text-slate-300 text-xs hover:text-white disabled:opacity-50"
                        >
                            <RotateCw className="h-3.5 w-3.5" />
                            Döndür
                        </button>
                    </>
                ) : null}
            </div>
            {preview ? (
                <p className="mt-3 text-[10px] text-slate-500">
                    Fotoğraf yan görünüyorsa döndürün; yüklemede otomatik düzeltme de uygulanır.
                </p>
            ) : null}
        </div>
    );
}

export function AiAnalyzeButton({ loading, onClick, children }) {
    return (
        <button
            type="button"
            disabled={loading}
            onClick={onClick}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#D4AF37] text-black text-sm font-bold disabled:opacity-50 hover:brightness-110 transition"
        >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {children || "Akıl ile Oku"}
        </button>
    );
}
